import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Button, TextField, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { createInvestorUrl, getInvestorUrls, deleteInvestorUrl, expireSlug, getCustomSlugRequests } from "./Helper";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BlockIcon from "@mui/icons-material/Block";
import Swal from "sweetalert2";
import { TablePagination } from "@mui/material";
import CircularProgress from '@mui/material/CircularProgress';

const GenerateInvestorUrl = () => {
  const context = useOutletContext();
  const [investorUrls, setInvestorUrls] = useState([]);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [update, setUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [slugCode, setSlugCode] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [expiryDateError, setExpiryDateError] = useState(false);
  
  const [requestedUrls, setRequestedUrls] = useState([]);
  const [requestedPage, setRequestedPage] = useState(1);
  const [requestedPageLimit, setRequestedPageLimit] = useState(10);
  const [requestedTotal, setRequestedTotal] = useState(0);
  const [isLoadingRequested, setIsLoadingRequested] = useState(false);
  
  const [selectedTab, setSelectedTab] = useState("generated");

  const getStatusForAPI = (filterValue) => {
    if (!filterValue || filterValue === "ALL") {
      return null;
    }
    return filterValue.toUpperCase();
  };

  const handleChangePage = (event, newPage) => {
    const apiPage = newPage + 1;
    const status = getStatusForAPI(statusFilter);
    fetchData(apiPage, pageLimit, status);
    setPage(apiPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageLimit(parseInt(event.target.value, 10));
    setPage(1);
    const status = getStatusForAPI(statusFilter);
    fetchData(1, parseInt(event.target.value, 10), status);
  };

  const handleStatusFilterChange = (event) => {
    const selectedValue = event.target.value;
    setStatusFilter(selectedValue);
    setPage(1);
    const status = getStatusForAPI(selectedValue);
    fetchData(1, pageLimit, status);
  };

  const fetchData = (pageNum, limit, status = null) => {
    setIsLoading(true);
    getInvestorUrls(pageNum, limit, status)
      .then((data) => {
        setIsLoading(false);
        let items = [];
        let totalCount = 0;
        
        const response = data?.data || data;
        
        if (response) {
          if (response.success && response.data) {
            items = response.data.rows || response.data.data || (Array.isArray(response.data) ? response.data : []);
            totalCount = response.data.totalItems || 
                        response.data.total || 
                        response.data.totalCount ||
                        response.data.count ||
                        response.total ||
                        response.totalItems ||
                        0;
          }
          else if (response.data) {
            if (Array.isArray(response.data)) {
              items = response.data;
            } else if (response.data.rows) {
              items = response.data.rows;
            } else if (response.data.items) {
              items = response.data.items;
            } else if (Array.isArray(response.data.data)) {
              items = response.data.data;
            }
            
            totalCount = response.data.totalItems || 
                        response.data.total || 
                        response.data.totalCount ||
                        response.data.count ||
                        response.totalItems ||
                        response.total ||
                        response.totalCount ||
                        response.count ||
                        0;
          }
          else if (Array.isArray(response)) {
            items = response;
            totalCount = data.total || 
                        data.totalItems || 
                        data.totalCount ||
                        data.count ||
                        data.meta?.total ||
                        items.length;
          }
          else if (response.meta) {
            items = response.data || response.rows || response.items || [];
            totalCount = response.meta.total || 
                        response.meta.totalItems ||
                        response.total ||
                        response.totalItems ||
                        0;
          }
        }
        if (totalCount === 0 && items.length > 0) {
          if (items.length === limit) {
            totalCount = pageNum * limit + 1;
          } else {
            totalCount = (pageNum - 1) * limit + items.length;
          }
        }
        
        setInvestorUrls(items);
        setTotal(totalCount);
      })
      .catch((err) => {
        console.log("Error:", err);
        setIsLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch investor URLs",
        });
      });
  };

  const fetchRequestedUrls = (pageNum, limit) => {
    setIsLoadingRequested(true);
    getCustomSlugRequests(pageNum, limit)
      .then((data) => {
        setIsLoadingRequested(false);
        let items = [];
        let totalCount = 0;
        
        const response = data?.data || data;
        
        if (response) {
          if (response.success && response.data) {
            items = Array.isArray(response.data) ? response.data : [];
            totalCount = response.meta?.total || response.data.total || 0;
          } else if (response.data) {
            if (Array.isArray(response.data)) {
              items = response.data;
            }
            totalCount = response.meta?.total || response.total || 0;
          } else if (Array.isArray(response)) {
            items = response;
            totalCount = items.length;
          } else if (response.meta) {
            items = response.data || [];
            totalCount = response.meta.total || 0;
          }
        }
        
        setRequestedUrls(items);
        setRequestedTotal(totalCount);
      })
      .catch((err) => {
        console.log("Error:", err);
        setIsLoadingRequested(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch requested URLs",
        });
      });
  };

  const handleRequestedChangePage = (event, newPage) => {
    const apiPage = newPage + 1;
    fetchRequestedUrls(apiPage, requestedPageLimit);
    setRequestedPage(apiPage);
  };

  const handleRequestedChangeRowsPerPage = (event) => {
    setRequestedPageLimit(parseInt(event.target.value, 10));
    setRequestedPage(1);
    fetchRequestedUrls(1, parseInt(event.target.value, 10));
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  useEffect(() => {
    context(["Generate Investor URL"]);
    const status = getStatusForAPI(statusFilter);
    fetchData(page, pageLimit, status);
    fetchRequestedUrls(requestedPage, requestedPageLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update]);

  const handleGenerate = async () => {
    if (!expiryDate) {
      setExpiryDateError(true);
      return;
    }
    setExpiryDateError(false);

    // Validate slug if provided
    if (slugCode && slugCode.trim() !== "") {
      const trimmedSlug = slugCode.trim();
      if (trimmedSlug.length < 10) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Slug code must be at least 10 characters",
        });
        return;
      }
      // Validate slug format: alphanumeric, hyphens, and underscores only
      const slugRegex = /^[a-zA-Z0-9_-]+$/;
      if (!slugRegex.test(trimmedSlug)) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Slug code can only contain letters, numbers, hyphens, and underscores",
        });
        return;
      }
    }

    const expiryDateTime = new Date(expiryDate);
    expiryDateTime.setHours(23, 59, 59, 0);
    const expiryDateISO = expiryDateTime.toISOString();

    setIsGenerating(true);
    try {
      const requestBody = {
        type: "INVESTOR_RELATION",
        expiry_date: expiryDateISO,
        metadata: {
          test: true,
          purpose: "investor-relation"
        }
      };
      if (slugCode && slugCode.trim() !== "") {
        requestBody.slug = slugCode.trim();
      }

      const response = await createInvestorUrl(requestBody);

      if (response?.data?.success || response?.status === 200 || response?.status === 201) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Investor URL generated successfully!",
        });
        setSlugCode("");
        setExpiryDate("");
        setUpdate(!update);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.data?.message || "Failed to generate investor URL",
        });
      }
    } catch (error) {
      console.error("Error generating investor URL:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Failed to generate investor URL. Please try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = (url) => {
    const baseUrl = process.env.REACT_APP_BASE_FE_URL;
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      Swal.fire({
        icon: "success",
        title: "Copied!",
        text: "URL copied to clipboard",
        timer: 1500,
        showConfirmButton: false,
      });
    });
  };

  const handleExpire = (slugId) => {
    Swal.fire({
      title: "Do you want to expire this Investor URL?",
      text: "This action will mark the URL as expired",
      showCancelButton: true,
      confirmButtonText: "Expire",
      confirmButtonColor: "#2EBAA3",
    }).then((result) => {
      if (result.isConfirmed) {
        expireSlug(slugId)
          .then((data) => {
            if (data?.data?.success || data?.status === 200 || data?.status === 201) {
              Swal.fire("Expired", "The URL has been marked as expired", "success");
              setUpdate(!update);
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: data?.data?.message || "Failed to expire investor URL",
              });
            }
          })
          .catch((err) => {
            console.log(err);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: err?.response?.data?.message || "Failed to expire investor URL",
            });
          });
      }
    });
  };

  const handleDelete = (slugId) => {
    Swal.fire({
      title: "Do you want to delete this Investor URL?",
      text: "This action cannot be undone",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteInvestorUrl(slugId)
          .then((data) => {
            if (data?.data?.success || data?.status === 200 || data?.status === 204) {
              Swal.fire("Deleted", "", "success");
              setUpdate(!update);
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: data?.data?.message || "Failed to delete investor URL",
              });
            }
          })
          .catch((err) => {
            console.log(err);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: err?.response?.data?.message || "Failed to delete investor URL",
            });
          });
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const isExpired = (expiryDateString) => {
    if (!expiryDateString) return false;
    const expiry = new Date(expiryDateString);
    const now = new Date();
    return expiry < now;
  };

  return (
    <div className="p-5 shadow-md bg-white">
      <div className="mb-6">
        <h3
          className="text-lg uppercase tracking-wider font-bold mb-4"
          style={{ color: "#2EBAA3" }}
        >
          Generate Investor URL
        </h3>
        
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <TextField
                label="Slug Code (Optional)"
                value={slugCode}
                onChange={(e) => {
                  setSlugCode(e.target.value);
                }}
                placeholder="Leave empty to auto-generate"
                fullWidth
                size="small"
                helperText={
                  slugCode.length > 0
                    ? slugCode.length < 10
                      ? `Minimum 10 characters required (${slugCode.length}/10) - Use letters, numbers, hyphens, and underscores only`
                      : `${slugCode.length} characters - Use letters, numbers, hyphens, and underscores only`
                    : "If left empty, a random slug will be generated"
                }
                error={
                  slugCode.length > 0 &&
                  (slugCode.length < 10 || !/^[a-zA-Z0-9_-]+$/.test(slugCode))
                }
              />
            </div>
            <div>
              <TextField
                label="Expiry Date"
                type="date"
                value={expiryDate}
                onChange={(e) => {
                  setExpiryDate(e.target.value);
                  setExpiryDateError(false); // Clear error when user selects a date
                }}
                fullWidth
                size="small"
                required
                error={expiryDateError}
                helperText={expiryDateError ? "Expiry date is required" : ""}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: new Date().toISOString().split("T")[0],
                }}
              />
            </div>
            <div className="flex items-start">
              <Button
                type="submit"
                variant="contained"
                style={{ backgroundColor: "#2EBAA3", color: "white" }}
                onClick={handleGenerate}
                disabled={isGenerating}
                fullWidth
              >
                {isGenerating ? (
                  <CircularProgress size={20} style={{ color: "white" }} />
                ) : (
                  "Generate URL"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {/* Tabs with Filter */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex border-b border-gray-300">
            <button
              className={`py-2 px-6 ${selectedTab === "generated" ? "border-b-2 border-[#2EBAA3] text-[#2EBAA3] font-bold" : "text-gray-500 font-bold"}`}
              onClick={() => handleTabChange("generated")}
            >
              GENERATED INVESTOR URLS
            </button>
            <button
              className={`py-2 px-6 ml-4 ${selectedTab === "requested" ? "border-b-2 border-[#2EBAA3] text-[#2EBAA3] font-bold" : "text-gray-500 font-bold"}`}
              onClick={() => handleTabChange("requested")}
            >
              REQUESTED URLS
            </button>
          </div>
          {selectedTab === "generated" && (
            <FormControl size="small" style={{ minWidth: 150 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="EXPIRED">Expired</MenuItem>
              </Select>
            </FormControl>
          )}
        </div>

        {/* Generated Investor URLs Tab Content */}
        {selectedTab === "generated" && (
          <>
        {isLoading ? (
          <div className="flex justify-center items-center py-10">
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto relative">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3 px-6 uppercase">
                      ID
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase">
                      URL
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase">
                      Slug Code
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase">
                      Created Date
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase">
                      Expiry Date
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase text-center">
                      Status
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {investorUrls?.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="py-8 px-6 text-center text-gray-500">
                        No investor URLs generated yet
                      </td>
                    </tr>
                  ) : (
                    investorUrls?.map((item, index) => {
                      const apiStatus = item.status || item.Status || null;
                      const isExpiredStatus = apiStatus === "EXPIRED" || apiStatus === "Expired";
                      const isDeletedStatus = apiStatus === "DELETED" || apiStatus === "Deleted";
                      const expiredByDate = isExpired(item.expiryDate || item.expiry_date);
                      const expired = isExpiredStatus || (!isDeletedStatus && expiredByDate);
                      const itemUrl = item.url || `/investor-relations?id=${item.slug}`;
                      return (
                        <tr className="bg-white border-b" key={item?.id || item?.slug_id || item?._id || index}>
                          <th
                            scope="row"
                            className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap"
                          >
                            {(page - 1) * pageLimit + index + 1}
                          </th>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <span className="text-blue-600 break-all">
                                {itemUrl}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className="font-mono text-sm">{item.slug}</span>
                          </td>
                          <td className="py-4 px-6">
                            {formatDate(item.createdAt || item.created_at || item.created_date)}
                          </td>
                          <td className="py-4 px-6">
                            {isExpiredStatus ? (
                              <span className="text-gray-400"></span>
                            ) : (
                              <span className={expired ? "text-red-600 font-semibold" : ""}>
                                {formatDate(item.expiryDate || item.expiry_date || item.expiryDate)}
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-6 text-center">
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${
                                isDeletedStatus
                                  ? "bg-gray-100 text-gray-800"
                                  : isExpiredStatus || expired
                                  ? "bg-red-100 text-red-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {isDeletedStatus ? "Deleted" : isExpiredStatus || expired ? "Expired" : "Active"}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-center">
                            <div className="flex justify-center gap-2">
                              <IconButton
                                className="w-fit"
                                aria-label="copy"
                                onClick={() => {
                                  const urlToCopy = item.url || `/investor-relations?id=${item.slug}`;
                                  handleCopy(urlToCopy);
                                }}
                                title="Copy URL"
                              >
                                <ContentCopyIcon className="text-blue-600" />
                              </IconButton>
                              {!expired && !isDeletedStatus && (
                                <IconButton
                                  className="w-fit"
                                  aria-label="expire"
                                  onClick={() => handleExpire(item.id || item.slug_id || item._id)}
                                  title="Expire URL"
                                >
                                  <BlockIcon className="text-orange-600" />
                                </IconButton>
                              )}
                              <IconButton
                                className="w-fit"
                                aria-label="delete"
                                onClick={() => handleDelete(item.id || item.slug_id || item._id)}
                                title="Delete URL"
                              >
                                <DeleteIcon className="text-red-600" />
                              </IconButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <TablePagination
              component="div"
              count={total}
              page={page - 1}
              onPageChange={handleChangePage}
              rowsPerPage={pageLimit}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          </>
        )}
          </>
        )}

        {/* Requested URLs Tab Content */}
        {selectedTab === "requested" && (
          <>
            {isLoadingRequested ? (
          <div className="flex justify-center items-center py-10">
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto relative">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3 px-6 uppercase">
                      ID
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase">
                      Name
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase">
                      Email
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase">
                      Company Name
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase">
                      Phone
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase text-center">
                      Status
                    </th>
                    <th scope="col" className="py-3 px-6 uppercase">
                      Created Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {requestedUrls?.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="py-8 px-6 text-center text-gray-500">
                        No requested URLs found
                      </td>
                    </tr>
                  ) : (
                    requestedUrls?.map((item, index) => {
                      const status = item.status || "PENDING";
                      return (
                        <tr className="bg-white border-b" key={item?.id || index}>
                          <th
                            scope="row"
                            className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap"
                          >
                            {(requestedPage - 1) * requestedPageLimit + index + 1}
                          </th>
                          <td className="py-4 px-6">
                            {item.name || "-"}
                          </td>
                          <td className="py-4 px-6">
                            {item.email || "-"}
                          </td>
                          <td className="py-4 px-6">
                            {item.companyName || "-"}
                          </td>
                          <td className="py-4 px-6">
                            {item.phone || "-"}
                          </td>
                          <td className="py-4 px-6 text-center">
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${
                                status === "PENDING"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : status === "APPROVED"
                                  ? "bg-green-100 text-green-800"
                                  : status === "REJECTED"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {status}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            {formatDate(item.created_at || item.createdAt)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <TablePagination
              component="div"
              count={requestedTotal}
              page={requestedPage - 1}
              onPageChange={handleRequestedChangePage}
              rowsPerPage={requestedPageLimit}
              onRowsPerPageChange={handleRequestedChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default GenerateInvestorUrl;

